import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { GradingStateService } from './services/grading-state.service';
import { GradingSessionService } from './services/grading-session.service';
import { GradingMessageService } from './services/grading-message.service';
import { FilesService } from './services/files.service';
import { GradingBadgeService } from './services/grading-badge.service';
import { ChatHeaderComponent } from './components/chat-header/chat-header.component';
import { ChatMessagesListComponent } from './components/chat-messages-list/chat-messages-list.component';
import { ChatInputComponent } from './components/chat-input/chat-input.component';
import { ChatLoadingComponent } from './components/chat-loading/chat-loading.component';
import { ChatErrorComponent } from './components/chat-error/chat-error.component';
import { LucideAngularModule, FileImage } from 'lucide-angular';

@Component({
  selector: 'app-grading-chat',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ChatHeaderComponent,
    ChatMessagesListComponent,
    ChatInputComponent,
    ChatLoadingComponent,
    ChatErrorComponent,
  ],
  providers: [
    GradingStateService,
    GradingSessionService,
    GradingMessageService,
    FilesService,
    GradingBadgeService,
  ],
  templateUrl: './grading-chat.component.html',
  styleUrl: './grading-chat.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GradingChatComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stateService = inject(GradingStateService);
  private readonly sessionService = inject(GradingSessionService);
  private readonly messageService = inject(GradingMessageService);
  private readonly filesService = inject(FilesService);

  protected readonly icons = { FileImage };

  // Reference to chat input for setting predefined messages
  private readonly chatInput = viewChild<ChatInputComponent>('chatInput');

  // Observables from state service
  protected readonly messages$ = this.stateService.messages$;
  protected readonly pendingAttachments$ = this.filesService.pendingAttachments$;
  protected readonly isLoading$ = this.stateService.isLoading$;
  protected readonly isSending$ = this.stateService.isSending$;
  protected readonly error$ = this.stateService.error$;

  /**
   * Show examples link until user uploads at least one file
   * Checks both current session uploads and previously uploaded resources
   */
  protected readonly showExamplesLink$ = combineLatest([
    this.filesService.hasEverUploaded$,
    this.stateService.session$,
  ]).pipe(
    map(([hasUploadedInSession, session]) => {
      // Hide if user uploaded in current session
      if (hasUploadedInSession) return false;
      // Hide if session already has resources (from previous uploads)
      if (session?.resources && session.resources.length > 0) return false;
      return true;
    }),
  );

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const id = params.get('id');
        if (id) {
          // Load existing session
          this.sessionService.loadSession(id);
        } else {
          // Create new session and redirect to /grading/:id
          this.createAndRedirect();
        }
      });
  }

  protected onMessageSent(content: string): void {
    this.messageService.sendMessage(content);
  }

  protected onFilesSelected(files: File[]): void {
    const sessionId = this.sessionService.sessionId;
    if (sessionId) {
      this.filesService.uploadFiles(sessionId, files);
    }
  }

  protected onExamplesRequested(): void {
    const sessionId = this.sessionService.sessionId;
    if (sessionId) {
      this.filesService.loadExampleFiles(sessionId);
      // Prefill message with sample text
      this.chatInput()?.setMessage('Here are my lesson materials and student work');
    }
  }

  protected onAttachmentRemoved(localId: string): void {
    this.filesService.removePendingAttachment(this.sessionService.sessionId, localId);
  }

  protected goBack(): void {
    this.router.navigate(['/teacher/dashboard/grading']);
  }

  protected retry(): void {
    this.sessionService.clearSession();
    this.filesService.clearPendingAttachments();
    this.createAndRedirect();
  }

  /**
   * Create a new session and redirect to /grading/:id
   */
  private createAndRedirect(): void {
    this.sessionService
      .createSession()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (session) => {
          this.router.navigate(['/grading', session.id], { replaceUrl: true });
        },
        error: () => {
          // Error is already handled in the service
        },
      });
  }
}
